import React, { createElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ExternalLink as ExternalLinkContentfulModel } from 'api/contentful/contentTypes.cloverdotcom';
import { VisuallyHidden } from 'components/VisuallyHidden/VisuallyHidden';
import { isChildrenDefined } from 'utils/ChildrenCheck';
import { isEntry } from 'utils/EntryUtil';
import { getUrlWithParamsOrPdpRedirect, shouldRedirectExistingMerchant } from 'utils/ExistingMerchant';
import { testAttribute } from '../../utils/DataTestId';
import { applyEntryDataAttributes } from '../EntryWrapper/EntryDataWrapper';
import { TrackedLink } from '../TrackedLink/TrackedLink';
import type { IContentLink } from './IContentLink';
import { BooleanFilter } from 'utils/TypeUtils';
import { useReseller } from 'hooks/Reseller/useReseller';

interface IExternalLinkProps extends IContentLink<ExternalLinkContentfulModel> {
  titleTag?: string;
  dataTestIdPrefix?: string;
}

const ExternalLink: React.FC<IExternalLinkProps> = ({
  entry,
  target,
  rel,
  children,
  hideTitle,
  innerRef,
  titleTag = 'span',
  dataTestIdPrefix,
  ...props
}) => {
  const { resellerTemplate } = useReseller();
  const { t } = useTranslation();

  if (!isEntry(entry)) return null;

  const { newTab, hint } = entry.fields;
  let { title: linkText } = entry.fields;

  let href = getUrlWithParamsOrPdpRedirect(entry);
  const shouldRedirectToPDP = shouldRedirectExistingMerchant(entry);

  const isPhoneNumber = (href: string | undefined) => href && href.includes('tel:');

  if (resellerTemplate.resellerPhoneInfo?.phoneNumberLink && isPhoneNumber(href)) {
    linkText = resellerTemplate.resellerPhoneInfo.phoneNumberLink.linkText;
    href = resellerTemplate.resellerPhoneInfo.phoneNumberLink.url;
  }
  const dataTestId = dataTestIdPrefix
    ? `${dataTestIdPrefix}-${testAttribute(linkText)}`
    : `${testAttribute(linkText)}`;

  const childrenIsValid = isChildrenDefined(children);
  const contactSalesLabel =
    shouldRedirectToPDP &&
    !childrenIsValid &&
    createElement(titleTag, { 'data-testid': 'text' }, t('contact-sales'));
  const titleLabel = !contactSalesLabel && !hideTitle && linkText && createElement(titleTag, '', linkText);
  const ariaLabel = (!titleLabel && !contactSalesLabel && !childrenIsValid && linkText) || undefined;

  return (
    <TrackedLink
      ref={innerRef}
      href={href || undefined}
      target={target || (newTab && '_blank') || undefined}
      aria-label={ariaLabel}
      rel={[newTab && 'noopener', rel].filter(BooleanFilter).join(' ') || undefined}
      data-testid={dataTestId}
      {...props}
    >
      {contactSalesLabel}
      {titleLabel}
      {children}
      <VisuallyHidden>{hint}</VisuallyHidden>
    </TrackedLink>
  );
};

const Wrapper = applyEntryDataAttributes(ExternalLink);
type Wrapper = typeof Wrapper;
export { Wrapper as ExternalLink };
